import { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerCardInfo } from '../../../../components/custom_components/CardInfo'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import useOneOffPaymentWithObjectivesAndTargetBonusAndContractChanges from '../../../../graphql/one-off-payment/hooks/useOneOffPaymentWithObjectivesAndTargetBonusAndContractChanges'
import useTargetCorrectionPaymentByMeetingid from '../../../../graphql/target-correction-payment/hooks/useTargetCorrectionPaymentByMeetingid'
import {
  Country,
  CountrySettingsFragment,
  GovernmentJobTitle,
  MeetingStep,
  MeetingStepStatus,
  MeetingStepViewId,
  MeetingType,
} from '../../../../types'
import { ConclusionContractChanges } from '../../contract/ConclusionContractChanges'
import { ConclusionEvaluation } from '../../evaluation/ConclusionEvaluation'
import { ConclusionDevelopmentObjectives } from '../../okr/ConclusionDevelopmentObjectives'
import { ConclusionDismissedObjectives } from '../../okr/ConclusionDismissedObjectives'
import { ConclusionNewObjectives } from '../../okr/ConclusionNewObjectives'
import { ConclusionSelfreflection } from '../../selfreflection/ConclusionSelfreflection'
import {
  useFindBonusByEmployeeIdAndMeetingId,
  useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId,
} from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import {
  getFlexibleBonusUnknownCurrencyForConversion,
  toEligibleFlexibleBonusOverride,
} from '../shared/flexibleBonusUtil'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { AppraisalFlowContext } from '../shared/AppraisalFlowProvider'
import { MeetingConfirmBtn } from '../shared/MeetingStepButton'
import { generateStepName, getIncompleteStepReason } from '../shared/util'
import { ConclusionPayment } from './ConclusionPayment'
import { ManagerCloseMeetingModal } from './ManagerCloseMeetingModal'
import { canConfirmMeeting } from './util/conclusionUtil'
import { useAuth } from '../../../../context/auth-context'
import { isUserHrForCountry } from '../../../../shared/rolesUtil'
import { useGovernmentJobTitlesByHiringOfficeId } from '../../../../graphql/government-job-title/hooks/useGovernmentJobTitlesByHiringOfficeId'
import { useCurrency } from '../../../../context/currency-context'
import { useSkillStatusBlocksMeeting } from '../../../../graphql/skill-status/useSkillStatusQueries'
import { useScoreGrid } from '../../../../graphql/score-grid/hooks/useScoreGrid'

interface AppraisalMeetingStepConclusion extends MeetingStepDefaultProps {
  countrySettings: CountrySettingsFragment
}

const AppraisalMeetingStepConclusion: FC<AppraisalMeetingStepConclusion> = ({ meeting, countrySettings, backBtn }) => {
  const { pastTargetsReferenceYear } = meeting.targetsCalculationHelper
  const [t] = useTranslation()
  const { meetingTypeSteps } = useContext(AppraisalFlowContext)
  const [open, changeOpen] = useState(false)
  const { user } = useAuth()
  const employeeCountry = meeting.employee.currentContract!.legalEntity.country
  const hasEvaluationStep = !!meeting.meetingSteps && meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.Evaluation)

  const { currencyConversionData } = useCurrency()

  const { loading, error, data } = useOneOffPaymentWithObjectivesAndTargetBonusAndContractChanges(
    meeting.employee.id,
    meeting.id,
    pastTargetsReferenceYear,
    meeting.targetsCalculationHelper.newTargetsReferenceYear,
    employeeCountry,
    true,
    countrySettings ? countrySettings.hasGovernmentJobTitleReferenceLevel : false,
  )

  const { loading_tcp, error_tcp, data_tcp } = useTargetCorrectionPaymentByMeetingid(meeting.id)

  const {
    data: findBonusAmountData,
    error: findBonusAmountDataError,
    loading: findBonusAmountLoading,
  } = useFindBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id)

  const isAppraisal = meeting.type === MeetingType.Appraisal
  const {
    data: pastYearFlexibleBonusData,
    loading: pastYearFlexibleBonusLoading,
    error: pastYearFlexibleBonusError,
  } = useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id, !isAppraisal)

  const {
    data: scoreGridData,
    loading: scoreGridLoading,
    error: scoreGridError,
  } = useScoreGrid(meeting.id, meeting.employee.id, meeting.appraisalSeason.country as Country, hasEvaluationStep)

  const { loading: skillStatusLoading, error: skillStatusError, data: skillStatusData } = useSkillStatusBlocksMeeting(meeting.id)
  const [getGovernmentJobTitles, { data: gjtData, loading: gjtLoading, error: gjtError }] = useGovernmentJobTitlesByHiringOfficeId()

  useEffect(() => {
    if (
      !!data &&
      !!data.currentContract &&
      !!data.currentContract.contractGovernmentJobTitle &&
      countrySettings.hasGovernmentJobTitleReferenceLevel
    ) {
      getGovernmentJobTitles({
        variables: {
          hiringOfficeId: data.currentContract.contractGovernmentJobTitle.governmentJobTitle.hiringOffice.id,
          excludePartnerLevels: true,
        },
      })
    }
  }, [getGovernmentJobTitles, countrySettings, data])

  if (
    loading ||
    gjtLoading ||
    error ||
    gjtError ||
    !data ||
    !meeting.meetingSteps ||
    !data.currentContract ||
    loading_tcp ||
    error_tcp ||
    !data_tcp ||
    !data_tcp.targetCorrectionPaymentAllowedYears ||
    !findBonusAmountData ||
    findBonusAmountDataError ||
    findBonusAmountLoading ||
    skillStatusLoading ||
    skillStatusError ||
    !skillStatusData ||
    (hasEvaluationStep && (scoreGridLoading || scoreGridError || !scoreGridData || !scoreGridData.scoreGridWithHistory)) ||
    (isAppraisal && (pastYearFlexibleBonusLoading || pastYearFlexibleBonusError))
  ) {
    return (
      <LoadingAndErrors
        isLoading={
          loading ||
          findBonusAmountLoading ||
          loading_tcp ||
          gjtLoading ||
          skillStatusLoading ||
          scoreGridLoading ||
          pastYearFlexibleBonusLoading
        }
        hasNoData={
          !data ||
          !data_tcp ||
          !data_tcp.targetCorrectionPaymentAllowedYears ||
          !skillStatusData ||
          !scoreGridData ||
          !scoreGridData.scoreGridWithHistory
        }
        error={
          error_tcp || error || findBonusAmountDataError || gjtError || skillStatusError || scoreGridError || pastYearFlexibleBonusError
        }
      />
    )
  }

  const skillStatusBlocksMeeting: boolean = skillStatusData.skillStatusBlocksMeeting
  const isOneOffPaymentNotAllowed = data.isOneOffPaymentNotAllowed
  const targetCorrectionPaymentAllowedYears = data_tcp.targetCorrectionPaymentAllowedYears
  const activeTargetCorrectionPayment = data_tcp.targetCorrectionPaymentByMeetingId
  const existingDevObjectivesToBeClosed = data.existingDevObjectivesToBeClosedInMeeting
  const invalidScoreGrid =
    hasEvaluationStep &&
    !!scoreGridData!.scoreGridWithHistory.scoreGoals.find(
      (scoreGoal) => typeof scoreGoal.resultEvaluation !== 'number' || scoreGoal.resultEvaluation < 1 || scoreGoal.resultEvaluation > 10,
    )

  const currentYearFlexibleBonus = findBonusAmountData.findBonusByEmployeeIdInAppraisalSeasonOfMeeting
  const currentYearFlexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    currentYearFlexibleBonus,
    data.currentContract!.currencyForPayments,
    currencyConversionData,
  )
  const currentYearFlexibleBonusOverride = toEligibleFlexibleBonusOverride(
    currentYearFlexibleBonus,
    data.currentContract!.currencyForPayments,
    currencyConversionData,
    data.branchOfficeByEmployee.timezone,
    meeting.targetsCalculationHelper.newTargetsReferenceYear,
    data.newTargetsContractsInYear,
    data.newTargetsTargetBonusOfYear,
    data.contractChangesByEmployee,
  )

  const pastYearFlexibleBonus = pastYearFlexibleBonusData?.findPastYearFlexibleBonusByEmployeeIdAndMeetingId
  const pastYearFlexibleBonusOverride = toEligibleFlexibleBonusOverride(
    pastYearFlexibleBonus,
    data.currentContract!.currencyForPayments,
    currencyConversionData,
    data.branchOfficeByEmployee.timezone,
    meeting.targetsCalculationHelper.pastTargetsReferenceYear,
    data.contractsInYear,
    data.targetBonusOfYear,
    data.contractChangesByEmployee,
  )
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    pastYearFlexibleBonus,
    data.currentContract!.currencyForPayments,
    currencyConversionData,
  )

  const incompleteMeetingSteps: MeetingStep[] = meetingTypeSteps
    .map((step, index) => ({ ...step, position: index + 1 }))
    .filter((step) => step.viewId !== MeetingStepViewId.Conclusion && step.status !== MeetingStepStatus.Done)

  const { cannotConfirm, tooltipText } = canConfirmMeeting(
    currentYearFlexibleBonusOverride,
    pastYearFlexibleBonusOverride,
    flexibleBonusUnknownCurrency,
    currentYearFlexibleBonusUnknownCurrency,
    meeting,
    data,
    incompleteMeetingSteps.length === 0,
    isOneOffPaymentNotAllowed,
    targetCorrectionPaymentAllowedYears,
    activeTargetCorrectionPayment,
    existingDevObjectivesToBeClosed,
    countrySettings.hasGovernmentJobTitleReferenceLevel,
    currencyConversionData,
    skillStatusBlocksMeeting,
    invalidScoreGrid,
  )
  const hasPaymentStep = meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.Payments)
  const hasPastTargetsStep = meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.PastTargets)
  const hasNewTargetsStep = meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.NewTargets)
  const hasContractChangesStep = meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.ContractChanges)
  const hasSelfreflectionStep = meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.SelfReflection)

  const isHrForCountry = isUserHrForCountry(meeting.appraisalSeason.country, user.roles)

  const renderReason = ({ id, position, viewId, status }: MeetingStep) => (
    <li key={id}>
      <PerText mt="xxxxs">
        {t('appraisalMeeting.stepFeedback.infoCard.incompleteStep', {
          position,
          stepName: generateStepName(viewId, meeting.type),
          reason: getIncompleteStepReason(status),
        })}
      </PerText>
    </li>
  )

  return (
    <div data-e2e-appraisal-step-conclusion>
      <ManagerCloseMeetingModal visible={open} meeting={meeting} onCancel={changeOpen} />
      <MeetingBackground>
        <PerText appearance="subtitle" color="primary" mb="m">
          {t('appraisalMeeting.stepConclusion.heading')}
        </PerText>
        {incompleteMeetingSteps.length > 0 && (
          <>
            <PerCardInfo
              heading={t('appraisalMeeting.stepFeedback.infoCard.heading')}
              description={t('appraisalMeeting.stepFeedback.infoCard.description')}
              childrenMarginTop="xxxxs"
              icon={{ name: 'warning_fill' }}
            >
              <PerText appearance="info" ml="xxxl">
                <ul>{incompleteMeetingSteps.map(renderReason)}</ul>
              </PerText>
            </PerCardInfo>
            <PerSpacer mb="m" />
          </>
        )}
        <PerText appearance="info" mb="l">
          {t('appraisalMeeting.stepConclusion.header')}
        </PerText>

        {hasPaymentStep && (
          <ConclusionPayment
            country={employeeCountry}
            objectives={data.objectivesInMeeting}
            oneOffPayment={data.oneOffPaymentByMeetingId ? data.oneOffPaymentByMeetingId : undefined}
            objectivePayment={data.objectivePayment ? data.objectivePayment : undefined}
            targetCorrectionPayment={data_tcp.targetCorrectionPaymentByMeetingId ? data_tcp.targetCorrectionPaymentByMeetingId : undefined}
          />
        )}
        {(hasPastTargetsStep || hasNewTargetsStep) && (
          <>
            <ConclusionDevelopmentObjectives objectives={data.objectivesInMeeting} />
            <ConclusionDismissedObjectives employee={meeting.employee} meetingId={meeting.id} objectives={data.objectivesInMeeting} />
            <ConclusionNewObjectives
              employee={meeting.employee}
              meetingId={meeting.id}
              allowedNewTargets={meeting.allowedMonetaryTargets.newTargetsAllowed}
              objectives={data.objectivesInMeeting}
              isEmployeePrepared={meeting.employeePrepared}
            />
          </>
        )}
        {hasContractChangesStep && (
          <ConclusionContractChanges
            meeting={meeting}
            currentContract={data.currentContract}
            countrySettings={countrySettings}
            governmentJobTitles={
              !!gjtData && !!gjtData.governmentJobTitlesByHiringOfficeId
                ? (gjtData.governmentJobTitlesByHiringOfficeId as GovernmentJobTitle[])
                : undefined
            }
          />
        )}
        {hasEvaluationStep && <ConclusionEvaluation scoreGrid={scoreGridData!.scoreGridWithHistory} />}
        {hasSelfreflectionStep && <ConclusionSelfreflection meeting={meeting} userIsHR={isHrForCountry} />}
      </MeetingBackground>

      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        <MeetingConfirmBtn onClick={() => changeOpen(true)} disabled={cannotConfirm} tooltip={cannotConfirm ? tooltipText : undefined} />
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Conclusion} stepStatus={MeetingStepStatus.Visited} />
    </div>
  )
}

export default AppraisalMeetingStepConclusion
